import type { TenantRegion } from "@kovernow/types/tenant-web-config";
import { FocusHeading } from "@kovernow/react-ui/a11y/focus-heading";
import { ChevronRight } from "lucide-react";
import { useEffect } from "react";
import { REGION_CHOICE_KEY } from "../bootstrap/resolve-config";
import { useT } from "../core/use-t";

const ORDER: TenantRegion[] = ["sg", "hk"];

export function rememberRegion(region: TenantRegion): void {
  try {
    window.localStorage.setItem(REGION_CHOICE_KEY, region);
  } catch {
    // Storage blocked: the picker simply shows again next time.
  }
}

/**
 * app.kovernow.com (CAP-1): two full-card links, named in text (never a
 * flag alone). The choice is remembered in localStorage (W-0 decision).
 */
export function RegionPicker({ regions, path }: { regions: Partial<Record<TenantRegion, string>>; path: string }) {
  const t = useT();
  useEffect(() => {
    document.title = t("common.pageTitle", { page: t("regionPicker.title") });
  }, [t]);
  return (
    <main id="main" className="mx-auto flex min-h-dvh w-full max-w-xl flex-col justify-center gap-6 px-4 py-10">
      <FocusHeading>{t("regionPicker.title")}</FocusHeading>
      <p className="text-muted-foreground">{t("regionPicker.intro")}</p>
      <ul className="flex flex-col gap-3">
        {ORDER.filter((r) => regions[r]).map((r) => (
          <li key={r}>
            <a
              data-testid={`region-${r}`}
              href={regions[r]!.replace(/\/+$/, "") + path}
              onClick={() => rememberRegion(r)}
              className="flex min-h-16 items-center justify-between gap-4 rounded-lg border bg-card p-4 text-card-foreground hover:bg-muted"
            >
              <span className="flex flex-col">
                <span className="text-lg font-bold">{t(`regionPicker.${r}`)}</span>
                <span className="text-sm text-muted-foreground">{t(`regionPicker.${r}Hint`)}</span>
              </span>
              <ChevronRight aria-hidden="true" className="size-5" />
            </a>
          </li>
        ))}
      </ul>
    </main>
  );
}
